import type React from 'react';
import { useState, type MouseEvent } from 'react';
import styled, { useTheme } from 'styled-components';

import { Link } from 'react-router-dom';
import { useAppState } from 'state';

import type { Equipment } from 'app/types/graphql';

import Typography from 'app/ui/components/atoms/typography';
import DotDivider from 'app/ui/components/atoms/dot-divider';

import FilledBookmark from 'app/ui/components/atoms/icons/bookmark/filled';
import EmptyBookmark from 'app/ui/components/atoms/icons/bookmark/empty';
import { getEquipmentIcons } from 'app/ui/components/atoms/class-card/equipment-icons';

import Tick from 'app/ui/components/atoms/icons/tick';

export type Props = {
  lessonId: string;
  srcSet: string;
  title: string;
  to: string;
  intensity: string;
  isSaved: boolean;
  onSaveLesson: (lessonId: string) => Promise<{ isSaved: boolean }>;
  onUnsaveLesson: (lessonId: string) => Promise<{ isSaved: boolean }>;
  isCompleted: boolean;
  testId?: string;
  equipment?: Equipment[] | null;
  onClick?: () => void;
};

const MainContainer = styled.div`
  cursor: pointer;
  display: flex;
  flex-direction: column;
  position: relative;
  border-radius: 0.25rem;
  justify-content: space-between;
  width: 20rem;
  background: ${({ theme }) => theme.colors.veryGray};
  &:hover {
    background: ${({ theme }) => theme.colors.focoltone};
    color: ${({ theme }) => theme.colors.black};
  }
`;

const ImageContainer = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'isHover' && prop !== 'srcSet',
})<{ srcSet: string; isHover: boolean }>`
  aspect-ratio: calc(420 / 260);
  background-image: url(${({ srcSet }) => srcSet});
  background-size: cover;
  background-position: center;
  ${({ srcSet, theme }) => !srcSet && `background-color: ${theme.colors.veryGray};`}
  ${({ isHover }) =>
    isHover &&
    `
    &::after {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      background:
        linear-gradient(180deg, rgba(0, 0, 0, 0) 63.23%, rgba(0, 0, 0, 0.3) 100%),
        linear-gradient(0deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.1));
    }
  `}
`;

const Title = styled(Typography)`
  position: relative;
  font-weight: 600;
  line-height: 1.5;
  color: inherit;
`;

const RPE = styled(Typography)`
  color: inherit;
`;

const ContentContainer = styled.div`
  padding: ${({ theme: { spacing } }) => spacing(4)};
  display: flex;
  width: 100%;
  align-items: flex-start;
  justify-content: space-between;
  flex-direction: row;
`;

const TextContent = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ExtraContent = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.25rem;
  flex-wrap: wrap;
`;

const EqIcons = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${({ theme: { spacing } }) => `${spacing(2)}`};
`;

const BookmarkWrapper = styled.div`
  ${({ theme: { spacing } }) => `
    width: ${spacing(6)};
    height: ${spacing(6)};
    margin-left: ${spacing(2)};

    svg {
      width: ${spacing(6)};
      height: ${spacing(6)};
    }
  `}
`;

const Label = styled.div`
  position: absolute;
  right: 1rem;
  top: 1rem;
  border-radius: 1rem;
  background: white;
  padding: 0.25rem 0.5rem;
  display: flex;
  gap: 0.25rem;
  color: black;
  opacity: 0.7;
  align-items: center;
`;

export const ClassCardContainer = styled.div`
  flex: 0 0 auto;
`;

const ClassCardComponent: React.FC<Props> = ({
  lessonId,
  srcSet,
  title,
  to,
  intensity,
  equipment = [],
  isSaved,
  isCompleted,
  testId,
  onClick,
  onSaveLesson,
  onUnsaveLesson,
}) => {
  const theme = useTheme();
  const userId = useAppState(({ auth }) => auth.userId);

  const [isHovered, setIsHovered] = useState(false);
  const [isLessonSaved, setIsLessonSaved] = useState(isSaved);

  const iconColor = isHovered ? theme.colors.black : theme.colors.white;
  const filledBookmarkColor = isHovered ? theme.colors.black : theme.colors.focoltone;

  const handleSaveLesson = async (event: MouseEvent) => {
    event.stopPropagation();
    if (userId && onSaveLesson) {
      try {
        const result = await onSaveLesson(lessonId);
        setIsLessonSaved(result.isSaved);
      } catch (error) {
        console.error('Error saving lesson:', error);
        setIsLessonSaved(false);
      }
    }
  };

  const handleUnsaveLesson = async (event: MouseEvent) => {
    event.stopPropagation();
    if (userId && onUnsaveLesson) {
      try {
        const result = await onUnsaveLesson(lessonId);
        setIsLessonSaved(result.isSaved);
      } catch (error) {
        console.error('Error unsaving lesson:', error);
      }
    }
  };

  const handleLinkClick = () => {
    if (onClick) {
      onClick();
    }
  };

  if (lessonId === 'rest-day') {
    return (
      <MainContainer>
        <ImageContainer srcSet={srcSet} isHover={false} />
        <ContentContainer>
          <TextContent>
            <Title variant="h5">{title}</Title>
          </TextContent>
        </ContentContainer>
      </MainContainer>
    );
  }

  return (
    <MainContainer
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onTouchStart={() => setIsHovered(true)}
      onTouchEnd={() => setIsHovered(false)}
      data-testid={testId}
    >
      <Link to={to} onClick={handleLinkClick}>
        <ImageContainer srcSet={srcSet} isHover={isHovered}>
          {isCompleted ? (
            <Label>
              <Tick color={theme.colors.black} />
            </Label>
          ) : null}
        </ImageContainer>
      </Link>
      <ContentContainer>
        <TextContent>
          <Link to={to} onClick={handleLinkClick}>
            <Title variant="h5">{title}</Title>
          </Link>
          <ExtraContent>
            <RPE variant="body2">{intensity}</RPE>
            {equipment && equipment.length > 0 && (
              <>
                <DotDivider color={iconColor} />
                <EqIcons>{getEquipmentIcons(equipment, iconColor)}</EqIcons>
              </>
            )}
          </ExtraContent>
        </TextContent>
        {isLessonSaved ? (
          <BookmarkWrapper onClick={handleUnsaveLesson}>
            <FilledBookmark color={filledBookmarkColor} />
          </BookmarkWrapper>
        ) : (
          <BookmarkWrapper onClick={handleSaveLesson}>
            <EmptyBookmark color={iconColor} />
          </BookmarkWrapper>
        )}
      </ContentContainer>
    </MainContainer>
  );
};

export default ClassCardComponent;
